import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";

import { Loading } from "../../../components";
import {
  clampTooltipPosition,
  filterPricesByRange,
  formatPriceChartDate,
  formatPriceChartPrice,
  getChartXDomain,
  getLimitedHistoryNotice,
  isValidListingLink,
  normalizePricesByDate,
} from "./priceChartUtils";
import "./price-chart.scss";

const LOWER_LIMIT = 500000;
const UPPER_LIMIT = 1000000;

const CHART_HEIGHT = 350;

// The plot box the previous charting library produced at this height: a 40px
// left gutter, a 10px top inset and a 40px bottom strip for the X axis. Recharts
// reaches the same box from a 40px YAxis, a 30px XAxis and these margins.
const CHART_MARGIN = { top: 10, right: 10, bottom: 10, left: 0 };
const Y_AXIS_WIDTH = 40;

// Mark RADIUS in px. Dense series (1000+ points) turned into a solid band at the
// old radius of 4 â€” 2 keeps the individual observations readable without hiding
// the line underneath.
const MARK_SIZE = 2;

const TRADE_COLOR = "#79E08F";
const MARKET_COLOR = "#7145D0";
const AREA_OPACITY = 0.18;
const GRID_STROKE = "rgba(255, 255, 255, 0.08)";
const X_TICK_COUNT = 6;
const Y_TICK_COUNT = 5;

// The old plot padded its auto Y domain by 10% of the data span on each side.
// Measured against the previous library, not assumed: a span of 800 gained 80
// units per side, a span of 100 gained 10. Recharts has no equivalent knob, so
// the same domain is computed here to keep the curve on the same pixels.
const Y_PADDING_RATIO = 0.1;

const SM_SCALE = 1 * 100;
const MD_SCALE = 1 * 100;
const LG_SCALE = 1000 * 100;

// Day-level tick labels, matching the "Jan 21" form the axis has always shown.
const X_TICK_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
});

const formatXTick = (value) => X_TICK_FORMAT.format(new Date(value));

// Grouped thousands, as the Y ticks have always been rendered ("1,000").
const Y_TICK_FORMAT = new Intl.NumberFormat("en-GB");

const formatYTick = (value) => Y_TICK_FORMAT.format(value);

// d3's tick algorithm, which the old axis picked its labels with. Recharts,
// handed a fixed domain, pins both endpoints and spaces from there â€” so the
// ticks are chosen here instead, landing on the round values (1,000 / 1,200 /
// 1,400 / 1,600) and therefore the grid lines the legacy plot drew.
const E10 = Math.sqrt(50);
const E5 = Math.sqrt(10);
const E2 = Math.sqrt(2);

const niceTicks = (domain, count) => {
  const [min, max] = domain || [];
  const rough = (max - min) / count;

  if (!Number.isFinite(rough) || rough <= 0) {
    return undefined;
  }

  const power = Math.floor(Math.log10(rough));
  const error = rough / 10 ** power;
  const factor = error >= E10 ? 10 : error >= E5 ? 5 : error >= E2 ? 2 : 1;
  const step = factor * 10 ** power;

  const ticks = [];
  for (let i = Math.ceil(min / step); i * step <= max; i += 1) {
    ticks.push(i * step);
  }

  return ticks;
};

const toDate = (dateStr) => {
  const normalized = String(dateStr).substr(0, 10);
  return new Date(
    Math.round(normalized.substr(0, 4)),
    Math.round(normalized.substr(5, 2)) - 1,
    Math.round(normalized.substr(8, 2))
  );
};

const findMinPrice = (prices) =>
  prices.reduce((acc, { min }) => Math.min(min, acc), prices?.[0]?.min);

const findMaxPrice = (prices) =>
  prices.reduce((acc, { max }) => Math.max(max, acc), prices?.[0]?.max);

// One row per observation, carrying only its own series' keys so the other
// series reads as a gap there and `connectNulls` bridges it. Rows are never
// merged or de-duplicated by date: two observations on the same day stay two
// rows, exactly as two points used to stay two points.
const buildSeriesRows = (data, scale, series) =>
  data.map((item) => ({
    x: toDate(item.date).getTime(),
    [`${series}Y`]: Math.round(item.average / scale),
    [`${series}Range`]: [
      Math.round(item.min / scale),
      Math.round(item.max / scale),
    ],
    priceRaw: item.average,
    dateRaw: item.date,
    listingLink: item.listing_link,
    clickable: isValidListingLink(item.listing_link),
    series,
  }));

const getAutoYDomain = (rows) => {
  let min = Infinity;
  let max = -Infinity;

  rows.forEach((row) => {
    const range = row.tradeRange || row.marketRange;
    const values = [row.tradeY, row.marketY, range?.[0], range?.[1]];

    values.forEach((value) => {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return;
      }

      min = Math.min(min, value);
      max = Math.max(max, value);
    });
  });

  if (min === Infinity) {
    return undefined;
  }

  const padding = (max - min) * Y_PADDING_RATIO;
  return [min - padding, max + padding];
};

const openListingLink = (listingLink) => {
  if (!isValidListingLink(listingLink)) {
    return;
  }

  window.open(listingLink.trim(), "_blank", "noopener,noreferrer");
};

const PriceChart = ({
  prices,
  dateRange = "max",
  isLoading = false,
  error = null,
}) => {
  const tooltipRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const filteredPrices = useMemo(
    () => filterPricesByRange(prices, dateRange),
    [prices, dateRange]
  );

  const xDomain = useMemo(
    () => getChartXDomain(dateRange, prices),
    [dateRange, prices]
  );

  const limitedHistoryNotice = useMemo(
    () => getLimitedHistoryNotice(prices, dateRange),
    [prices, dateRange]
  );

  const { market = [], trade = [] } = filteredPrices;
  const allPrices = useMemo(() => [...market, ...trade], [market, trade]);

  const repositionTooltip = useCallback((left, top) => {
    const tooltipEl = tooltipRef.current;
    const width = tooltipEl?.offsetWidth || 220;
    const height = tooltipEl?.offsetHeight || 40;

    return clampTooltipPosition(left, top, width, height);
  }, []);

  useEffect(() => {
    if (!tooltip) {
      return undefined;
    }

    const handleViewportChange = () => {
      setTooltip((current) => {
        if (!current) {
          return current;
        }

        const nextPosition = repositionTooltip(current.left, current.top);
        return { ...current, ...nextPosition };
      });
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [tooltip, repositionTooltip]);

  const clearTooltip = useCallback(() => {
    setTooltip(null);
  }, []);

  const showTooltip = useCallback(
    (point, event) => {
      if (!point) {
        return;
      }

      const offset = 14;
      const left = (event?.clientX || 0) + offset;
      const top = (event?.clientY || 0) + offset;
      const position = repositionTooltip(left, top);

      setTooltip({
        point,
        ...position,
      });
    },
    [repositionTooltip]
  );

  const handlePointClick = useCallback((point) => {
    if (!point?.clickable) {
      return;
    }

    openListingLink(point.listingLink);
  }, []);

  // Recharts renders a dot per row, including the rows belonging to the other
  // series â€” those resolve to a null coordinate and draw nothing.
  const renderDot = useCallback(
    (color) =>
      ({ key, cx, cy, payload }) => {
        if (typeof cx !== "number" || typeof cy !== "number") {
          return null;
        }

        return (
          <circle
            key={key}
            className="rc-price-chart__point"
            cx={cx}
            cy={cy}
            r={MARK_SIZE}
            fill={color}
            stroke={color}
            onMouseOver={(event) => showTooltip(payload, event)}
            onMouseOut={clearTooltip}
            onClick={() => handlePointClick(payload)}
          />
        );
      },
    [showTooltip, clearTooltip, handlePointClick]
  );

  if (isLoading) {
    return (
      <div className="rc-price-chart rc-price-chart__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rc-price-chart rc-price-chart__error">
        Unable to load price chart data.
      </div>
    );
  }

  if (!allPrices.length) {
    const hasAnyData = [
      ...normalizePricesByDate(prices).market,
      ...normalizePricesByDate(prices).trade,
    ].length;

    return (
      <div className="rc-price-chart rc-price-chart__empty">
        {hasAnyData ? "No data in selected range" : "Data Not Available"}
      </div>
    );
  }

  const minPrice = findMinPrice(allPrices);
  const maxPrice = findMaxPrice(allPrices);

  let scale = SM_SCALE;

  if (minPrice >= LOWER_LIMIT && maxPrice <= UPPER_LIMIT) {
    scale = MD_SCALE;
  }

  if (maxPrice > UPPER_LIMIT) {
    scale = LG_SCALE;
  }

  const yTitle = scale === LG_SCALE ? "Thousand GBP" : "GBP";

  const chartData = [
    ...buildSeriesRows(trade, scale, "trade"),
    ...buildSeriesRows(market, scale, "market"),
  ];

  const yDomain =
    minPrice === maxPrice
      ? [minPrice - 10, maxPrice + 10]
      : getAutoYDomain(chartData);

  const yTicks = niceTicks(yDomain, Y_TICK_COUNT);

  const chartClassName = `rc-price-chart${
    tooltip?.point?.clickable ? " rc-price-chart--clickable" : ""
  }`;

  return (
    <div className={chartClassName}>
      {limitedHistoryNotice && (
        <p className="rc-price-chart__notice">{limitedHistoryNotice}</p>
      )}
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart
          data={chartData}
          margin={CHART_MARGIN}
          onMouseLeave={clearTooltip}
        >
          <CartesianGrid
            vertical={false}
            stroke={GRID_STROKE}
            syncWithTicks
          />
          <Area
            dataKey="tradeRange"
            type="monotoneX"
            fill={TRADE_COLOR}
            stroke={TRADE_COLOR}
            fillOpacity={AREA_OPACITY}
            strokeOpacity={AREA_OPACITY}
            strokeWidth={0.6}
            connectNulls
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="tradeY"
            type="monotoneX"
            stroke={TRADE_COLOR}
            strokeWidth={2}
            connectNulls
            dot={renderDot(TRADE_COLOR)}
            activeDot={false}
            isAnimationActive={false}
          />
          <Area
            dataKey="marketRange"
            type="monotoneX"
            fill={MARKET_COLOR}
            stroke={MARKET_COLOR}
            fillOpacity={AREA_OPACITY}
            strokeOpacity={AREA_OPACITY}
            strokeWidth={1}
            connectNulls
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="marketY"
            type="monotoneX"
            stroke={MARKET_COLOR}
            strokeWidth={2}
            connectNulls
            dot={renderDot(MARKET_COLOR)}
            activeDot={false}
            isAnimationActive={false}
          />
          <XAxis
            dataKey="x"
            type="number"
            scale="time"
            domain={
              xDomain
                ? [xDomain[0].getTime(), xDomain[1].getTime()]
                : ["dataMin", "dataMax"]
            }
            allowDataOverflow
            tickCount={X_TICK_COUNT}
            tickFormatter={formatXTick}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            domain={yDomain}
            allowDataOverflow
            ticks={yTicks}
            tickFormatter={formatYTick}
            label={{ value: yTitle, position: "top" }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {tooltip?.point && (
        <div
          ref={tooltipRef}
          className="rc-price-chart-tooltip"
          role="tooltip"
          style={{
            left: `${tooltip.left}px`,
            top: `${tooltip.top}px`,
          }}
        >
          <span className="rc-price-chart-tooltip__label">Price:</span>
          <span className="rc-price-chart-tooltip__value">
            {formatPriceChartPrice(tooltip.point.priceRaw)}
          </span>
          <span className="rc-price-chart-tooltip__divider" aria-hidden="true">
            |
          </span>
          <span className="rc-price-chart-tooltip__label">Date:</span>
          <span className="rc-price-chart-tooltip__value">
            {formatPriceChartDate(tooltip.point.dateRaw)}
          </span>
        </div>
      )}
    </div>
  );
};

export default PriceChart;
